import React from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Timer, Gauge, ClipboardCheck, Hourglass, AlertTriangle } from "lucide-react";

const TYPE_LABELS = {
  commercial_property: "Comm. Property",
  workers_comp: "Workers' Comp",
  habitational: "Habitational",
  general_liability: "Gen. Liability",
  commercial_auto: "Comm. Auto",
};

const fmtDays = (v) => (v === null || v === undefined ? "—" : `${v}d`);

function AgingBuckets({ title, buckets, total, testid }) {
  const order = ["0-7", "8-14", "15-30", "31+"];
  const colors = { "0-7": "bg-green-500", "8-14": "bg-amber-400", "15-30": "bg-orange-500", "31+": "bg-red-500" };
  return (
    <div data-testid={testid}>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{title} <span className="text-slate-400 normal-case">({total} open)</span></p>
      <div className="grid grid-cols-4 gap-2">
        {order.map(k => (
          <div key={k} className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${colors[k]}`} />
              <span className="font-heading font-bold text-lg text-slate-800">{buckets?.[k] ?? 0}</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">{k} days</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SlaSection({ data }) {
  if (!data) return null;
  const cards = [
    { label: "Avg Ordered → Completed", value: fmtDays(data.avg_ordered_to_completed), sub: `median ${fmtDays(data.median_ordered_to_completed)}`, icon: Timer, color: "text-navy", bg: "bg-blue-50", testid: "sla-oc" },
    { label: "Avg Review Time", value: fmtDays(data.avg_completed_to_finalized), sub: "completed → finalized", icon: ClipboardCheck, color: "text-purple-600", bg: "bg-purple-50", testid: "sla-review" },
    { label: "Avg Total Turnaround", value: fmtDays(data.avg_total_turnaround), sub: "ordered → finalized", icon: Gauge, color: "text-green-600", bg: "bg-green-50", testid: "sla-total" },
    { label: "Overdue Open Surveys", value: data.overdue_open, sub: `${data.open_count} open · ${data.review_count} awaiting review`, icon: AlertTriangle, color: data.overdue_open > 0 ? "text-red-600" : "text-slate-500", bg: data.overdue_open > 0 ? "bg-red-50" : "bg-slate-100", testid: "sla-overdue" },
  ];
  const queue = [...(data.open_queue || []), ...(data.review_queue || [])].sort((a, b) => b.age_days - a.age_days).slice(0, 10);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="sla-section">
      <div>
        <h3 className="font-heading font-semibold text-slate-700 flex items-center gap-2">
          <Hourglass className="w-4 h-4 text-navy" /> Turnaround & SLA
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">Cycle times across {data.completed_count} completed surveys, plus aging queues for everything still open</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className="border border-slate-100 rounded-xl p-4" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{c.label}</span>
              <div className={`w-7 h-7 ${c.bg} rounded-lg flex items-center justify-center`}>
                <c.icon className={`w-3.5 h-3.5 ${c.color}`} />
              </div>
            </div>
            <div className="font-heading text-2xl font-bold text-slate-800">{c.value}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {data.trend?.length > 1 && (
        <div data-testid="sla-trend-chart">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Cycle Time Trend (ordered → completed)</p>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={data.trend}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="count" name="Surveys completed" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" dataKey="avg_days" name="Avg days" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <AgingBuckets title="Awaiting Insured" buckets={data.open_aging_buckets} total={data.open_count} testid="aging-open" />
        <AgingBuckets title="Awaiting Underwriter Review" buckets={data.review_aging_buckets} total={data.review_count} testid="aging-review" />
      </div>

      {queue.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Oldest Items in Queue</p>
          <table className="w-full text-sm" data-testid="sla-queue-table">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                <th className="py-2">Insured</th><th>Type</th><th>Stage</th><th>Age</th><th>Due</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(r => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`sla-queue-row-${r.id}`}>
                  <td className="py-2 font-medium text-slate-700">{r.insured_name}</td>
                  <td className="text-slate-500 text-xs">{TYPE_LABELS[r.inspection_type] || r.inspection_type}</td>
                  <td>
                    <span className={`text-xs font-medium ${r.status === "completed" ? "text-amber-600" : "text-slate-500"}`}>
                      {r.status === "completed" ? "Needs review" : "With insured"}
                    </span>
                  </td>
                  <td>
                    <span className={`text-xs font-semibold ${r.age_days > 30 ? "text-red-600" : r.age_days > 14 ? "text-orange-600" : "text-slate-600"}`}>
                      {r.age_days}d
                    </span>
                  </td>
                  <td className="text-xs">
                    {r.due_date ? (
                      <span className={r.overdue ? "text-red-600 font-semibold" : "text-slate-400"}>
                        {r.due_date}{r.overdue ? " · OVERDUE" : ""}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
